'use client';

import { incrementTelemetry } from 'core-events/actions';
import { JSX, useContext, useEffect, useState } from 'react';
import { getContentClassByContentType, getSearchUrl, SearchFilterContentTypes } from 'search-gateway-client';
import { SearchOrigins } from 'search-gateway-client';
import { useStatsigExperimentContext } from 'statsig/components';

import CloseIcon from '@videoblocks/react-icons/Close';
import TimeClockCircle from '@videoblocks/react-icons/TimeClockCircle';

import { NavSearchContext } from '../../context/NavSearchContext';
import { SearchBarVariant } from '../NavSearchElements/searchUtils';
import { SearchEntry, SearchHistoryType } from './utils';

interface SearchHistoryProps {
    contentType: SearchFilterContentTypes;
    children: React.ReactNode;
}

const searchBarVariantStyle = {
    [SearchBarVariant.TopNav]: 'py-2 w-[500px]',
};

function SearchHistory({ contentType, children }: SearchHistoryProps): JSX.Element {
    const context = useStatsigExperimentContext();
    const searchBarContext = useContext(NavSearchContext);
    const { searchBarVariant } = searchBarContext;
    const [parsedHistory, setParsedHistory] = useState<SearchHistoryType>({});
    const [history, setHistory] = useState<SearchEntry[]>([]);
    const contentClass = getContentClassByContentType(contentType);
    const header = contentClass.charAt(0).toUpperCase() + contentClass.slice(1);

    useEffect(() => {
        if (context?.isSearchHistoryEnabled) {
            const storedHistory = localStorage.getItem('searchHistory');

            if (storedHistory) {
                try {
                    const storedHistoryParsed = JSON.parse(storedHistory) as SearchHistoryType;
                    const contentTypeSearched = storedHistoryParsed[contentClass] || [];
                    // eslint-disable-next-line react-hooks/set-state-in-effect
                    setParsedHistory(storedHistoryParsed);
                    setHistory(contentTypeSearched.reverse());
                } catch (e) {
                    console.error('Invalid localStorage format:', e);
                }
            }
        }
    }, [contentClass]);

    if (!context?.isSearchHistoryEnabled || history.length === 0) {
        return <>{children}</>;
    }

    return (
        <div className={searchBarVariantStyle[searchBarVariant as keyof typeof searchBarVariantStyle] ?? 'py'}>
            <div className="flex justify-between items-center border-b border-gray-300">
                <h3 className="font-normal text-xs font-inter text-gray-900 m-0 mb-2">Recent {header} Searches</h3>
                <button
                    className="bg-transparent border-none p-0 font-normal text-xs font-inter text-blue-700 m-0 mb-2"
                    role="button"
                    onClick={() => {
                        setHistory([]);
                        localStorage.removeItem('searchHistory');
                    }}
                >
                    Clear All
                </button>
            </div>
            <ul className="m-0">
                {history.map((searchEntry) => (
                    <li className="pt-4" key={searchEntry?.term}>
                        <div className="flex flex-row bg-blue-100 items-center text-blue-900 text-base font-semibold font-inter rounded-sm py-1 px-2 w-fit">
                            <button
                                className="bg-transparent border-none p-0 m-0 text-inherit font-inherit flex items-center"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const searchUrl = getSearchUrl({
                                        contentType,
                                        searchTerm: searchEntry?.term,
                                        searchOrigin: SearchOrigins.TYPE_AHEAD,
                                    });
                                    void incrementTelemetry('typeahead.searchHistory.click');
                                    window.location.href = searchUrl;
                                }}
                            >
                                <div className="w-3 h-3 border-none mr-2 fill-current">
                                    <TimeClockCircle />
                                </div>
                                {`"${searchEntry?.term}"`}
                            </button>
                            <button
                                id="clear-search-term-button"
                                type="button"
                                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                    e.stopPropagation();
                                    try {
                                        setHistory((prev) =>
                                            prev.filter(
                                                (prevSearchEntry) => prevSearchEntry.term !== searchEntry?.term,
                                            ),
                                        );
                                        parsedHistory[contentClass] = parsedHistory[contentClass].filter(
                                            (prevSearchEntry) => prevSearchEntry.term !== searchEntry?.term,
                                        );
                                        localStorage.setItem('searchHistory', JSON.stringify(parsedHistory));
                                    } catch (e) {
                                        console.error('Error updating history');
                                    }
                                }}
                                className="w-3 h-3 border-none ml-2 fill-current"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default SearchHistory;
